import React, { useCallback, useMemo } from 'react'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Typography from '@mui/material/Typography'
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg'
import { ReactComponent as MinusIcon } from 'assets/icons/minus.svg'
import { ReactComponent as MapPinIcon } from 'assets/icons/map-pin-light.svg'
import { ReactComponent as CheckmarkIcon } from 'assets/icons/checkmark.svg'
import Grid from '@mui/material/Grid2'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import { Button, Chip, ListItemSecondaryAction } from '@mui/material'
import ListItemButton from '@mui/material/ListItemButton'
import { AppContext, initialAppMasterData } from 'providers/context/App'
import { useSearchParams } from 'react-router'
import { getLocations } from 'services/Location'
import { createCart, getCart } from 'services/Cart'
import _, { chain, find } from 'lodash'
import { LocationPlace, LocationsRes } from 'utils/types/locationTypes'
import { useLoading } from 'providers/context/Loading'
import {
  MEMBERSHIP_APPOINTMENT_CONFIRMATION_ROUTE,
  ENHANCEMENT_ROUTE,
  FACIALS_ROUTE,
  MEMBERSHIP_CATEGORIES,
  INFLUENCER_FLOW_LOCATIONS,
} from 'utils/constants/Helpers'
import { AddToCartAnalytics } from 'services/Analytics'
import { anyEsth } from 'providers/context/EstheticianList'
import { useSnackbar } from 'notistack'
import { GENERAL_ERROR_MESSAGE } from 'utils/constants/Messages'
import {
  addSelectedItemToCart,
  addSelectedPurchasableItemToCart,
  getMembershipLocations,
} from 'services/Membership'
import { getOpeningDate } from 'utils/helper-functions'
import './locations.scss'
import { AvailableCategories, AvailableItems } from 'utils/types/cartTypes'
import { enhancementIcons } from 'utils/constants/enhancementOptions'
import { Enhancement } from 'utils/types/appTypes'
import {
  BI_MONTHLY_MATCHERS,
  MEMBERSHIP_TYPES,
} from 'utils/constants/Membership'
import useNavigate from 'utils/navigation/useCustomNavigate'
import { FACIALS } from 'utils/constants/Enums'
import { captureError } from 'utils/sentry/sentry-utils'
import {
  FAILED_FETCH_LOCATION_DETAILS,
  FAILED_MOVE_APPT_CONFIRM_ROUTE,
  FAILED_MOVE_FROM_LOCATION_SCREEN,
} from 'utils/sentry/sentry-messages'

type Props = {
  isMembership?: boolean
}

const Locations = ({ isMembership }: Props) => {
  const { isInfluencerFlow, navigate } = useNavigate()
  const { appMasterData, updateData: appUpdateData } =
    React.useContext(AppContext)
  const { setLoading } = useLoading()
  const [expanded, setExpanded] = React.useState('')
  const [locationList, setLocationList] = React.useState<LocationsRes[]>([])
  const [selectedLocation, setSelectedLocation] = React.useState('')
  const [searchParams] = useSearchParams()
  const { enqueueSnackbar } = useSnackbar()

  const handleChange =
    (panel: string) =>
    (event: React.SyntheticEvent<Element, Event>, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : '')
    }

  const getMembershipObj = useCallback(
    (categories: AvailableCategories[]) => {
      const memberships = categories
        .filter(({ name }) => MEMBERSHIP_CATEGORIES.includes(name))
        .flatMap(({ availableItems }) => availableItems)
        .filter((item) => item.__typename === 'CartAvailablePurchasableItem')

      const isBiMonthly = (membership: AvailableItems) =>
        BI_MONTHLY_MATCHERS.some((matcher) =>
          membership.name.toLowerCase().includes(matcher)
        )

      const monthlyMembership = memberships.find((m) => !isBiMonthly(m))

      const biMonthlyMembership = memberships.find((m) => isBiMonthly(m))

      if (
        appMasterData.userMembershipType === MEMBERSHIP_TYPES.BI_MONTHLY &&
        biMonthlyMembership
      ) {
        return biMonthlyMembership ?? monthlyMembership
      }

      return monthlyMembership
    },
    [appMasterData.userMembershipType]
  )

  const selectLocation = async (place: LocationPlace) => {
    setSelectedLocation(place.id)
    appUpdateData({
      location: {
        name: place.name ?? '',
        locationId: place.id ?? '',
        tz: place.timezone ?? '',
        mapImg: place.mapImg ?? '',
        advanceBookingLimit: place.advanceBookingLimit ?? 2,
        enableGroupBooking: place.enableGroupBooking ?? false,
        enableGhostBooking: place.enableGhostBooking ?? false,
        enableMembershipFlow: place.enableMembershipFlow ?? false,
        address: place.address ?? initialAppMasterData.location.address,
        contact: place.contact ?? initialAppMasterData.location.contact,
        marketingSupport:
          place.marketingSupport ??
          initialAppMasterData.location.marketingSupport,
        ...(place.openDate && { openDate: place.openDate }),
      },
      filteredEstheticians: [],
    })
  }

  const getLocation = async () => {
    const getLocationsApiCall = isMembership
      ? getMembershipLocations
      : getLocations
    try {
      setLoading(true)
      const locationData: LocationsRes[] = await getLocationsApiCall()
      setLocationList(locationData)
      if (appMasterData.location.locationId) {
        setSelectedLocation(appMasterData.location.locationId)
        const selectedRecord = find(locationData, {
          places: [{ id: appMasterData.location.locationId }],
        })
        setExpanded(selectedRecord?.locationId ?? '')
      }
      setLoading(false)
    } catch (error) {
      console.error(error)
      captureError(FAILED_FETCH_LOCATION_DETAILS, {
        location: appMasterData.location,
        error,
        functionName: 'getLocation',
      })
      enqueueSnackbar(GENERAL_ERROR_MESSAGE, {
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }
  const navigateToNextScreen = async () => {
    try {
      setLoading(true)
      const autoSelectDate = getOpeningDate(appMasterData.location)
      if (autoSelectDate) {
        appUpdateData({ selectedDate: autoSelectDate })
      }
      // Analytics
      AddToCartAnalytics()
      const createdCart = await createCart(appMasterData.location.locationId)
      const getFacialObj: AvailableItems = chain(
        createdCart.createCart.cart.availableCategories
      )
        .filter({ name: 'Facials' })
        .take()
        .first()
        .thru((x) =>
          find(x.availableItems, {
            name: isInfluencerFlow ? FACIALS.INFLUENCER : FACIALS.FIFTY_MIN,
          })
        )
        .value()

      const getCartValues = await getCart(
        createdCart.createCart.cart.id,
        getFacialObj.id
      )
      const staffVariants = getCartValues.cart.availableItem.staffVariants
      const enhancements =
        _.chain(getCartValues.cart.availableCategories)
          .find({ name: 'Enhancements' })
          .get('availableItems')
          .value() || []
      const availableFacials =
        _.chain(getCartValues.cart.availableCategories)
          .filter((val) => {
            return (
              val.name === 'Facials' ||
              val.name === 'Laser' ||
              val.name === 'Microneedling'
            )
          })
          .map('availableItems')
          .value()
          .flat()
          // If it's regular flow, Remove influencer facial from list
          .filter(
            (facial) => !isInfluencerFlow && facial.name !== FACIALS.INFLUENCER
          ) || []

      const membershipData = getMembershipObj(
        getCartValues.cart.availableCategories
      )

      const availableEnhancements = enhancements.map((item) => {
        const iconObj = enhancementIcons.find((obj) => obj.title === item.name)
        if (iconObj) {
          item = { ...item, ...iconObj }
        }
        return item
      }) as Enhancement[]

      staffVariants.unshift(anyEsth)

      appUpdateData({
        cart: { ...appMasterData.cart, cartId: createdCart.createCart.cart.id },
        category: { categoryId: getFacialObj.id, categoryObj: getFacialObj },
        availableEnhancements,
        availableFacials,
        availableEstheticians: staffVariants,
        selectedEnhancements: [],
        membershipData: { ...appMasterData.membershipData, ...membershipData },
        isLaserFacial: false,
        selectedFacial: '',
      })

      if (isInfluencerFlow) {
        return navigate(ENHANCEMENT_ROUTE)
      }
      // Available facials are more then 1 then go to facials screen or go to enhancements screen
      if (availableFacials.length > 1) {
        navigate(FACIALS_ROUTE)
      } else {
        navigate(ENHANCEMENT_ROUTE)
      }
    } catch (error) {
      console.error(error)
      captureError(FAILED_MOVE_FROM_LOCATION_SCREEN, {
        location: appMasterData.location,
        facial: appMasterData.category.categoryObj,
        error,
        functionName: 'navigateToNextScreen',
      })
      enqueueSnackbar(GENERAL_ERROR_MESSAGE, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const navigateToEstheticianMembership = async (id?: string) => {
    try {
      setLoading(true)
      const locationId = id ?? appMasterData.location.locationId
      // Analytics
      AddToCartAnalytics()
      const createdCart = await createCart(locationId)

      const getFacialId = getMembershipObj(
        createdCart.createCart.cart.availableCategories
      )?.id

      const cart = {
        ...initialAppMasterData.cart,
        cartId: createdCart.createCart.cart.id,
      }
      if (getFacialId) {
        appUpdateData({
          cart,
          category: { categoryId: getFacialId },
        })
        await addSelectedItemToCart(createdCart.createCart.cart.id, getFacialId)
        const response = await addSelectedPurchasableItemToCart(
          createdCart.createCart.cart.id,
          getFacialId
        )
        appUpdateData({
          cart: {
            ...cart,
            summary: response?.addCartSelectedPurchasableItem?.cart?.summary
              ? response?.addCartSelectedPurchasableItem?.cart?.summary
              : appMasterData.cart.summary,
          },
        })
      }
      navigate(MEMBERSHIP_APPOINTMENT_CONFIRMATION_ROUTE)
    } catch (error) {
      console.error(error)
      captureError(FAILED_MOVE_APPT_CONFIRM_ROUTE, {
        location: appMasterData.location,
        facial: appMasterData.category.categoryObj,
        functionName: 'navigateToEstheticianMembership',
        error,
      })
      enqueueSnackbar(GENERAL_ERROR_MESSAGE, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const userCustomNavigation = async (data?: string) => {
    if (!data) return

    const normalizedData = data.toLowerCase()

    for (const location of locationList) {
      const place = location.places.find(
        (p) => p.name.toLowerCase() === normalizedData
      )

      if (place) {
        try {
          await selectLocation(place)
        } catch (e) {
          console.error(e)
          return
        }

        if (!appMasterData.location.locationId) return

        if (isMembership) {
          navigateToEstheticianMembership(place.id)
        } else {
          navigateToNextScreen()
        }

        break
      }
    }
  }

  const userExpandRegion = (data?: string) => {
    locationList.forEach((location) => {
      if (location.locationName?.toLowerCase() === data?.toLowerCase()) {
        setExpanded(location.locationId)
      }
    })
  }
  React.useEffect(() => {
    getLocation().catch((e) => console.error(e))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    const customLocation = searchParams.get('city')
    const customRegion = searchParams.get('region')
    if (customLocation) {
      userCustomNavigation(customLocation)
    }
    if (customRegion) {
      userExpandRegion(customRegion)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationList, appMasterData.location.locationId])

  React.useEffect(() => {
    appUpdateData({ selectedDate: new Date() })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appMasterData.location.locationId])

  const filteredLocationsList = useMemo(() => {
    if (!isInfluencerFlow) return locationList

    return locationList.map((location) => ({
      ...location,
      places: location.places.filter((l) =>
        INFLUENCER_FLOW_LOCATIONS.includes(l.name)
      ),
    }))
  }, [locationList, isInfluencerFlow])

  return (
    <>
      <div className="location-selection">
        {filteredLocationsList.map((location) => {
          return (
            !!location.locationName &&
            !!location.places?.length && (
              <Accordion
                key={location.locationId}
                square
                expanded={expanded === location.locationId}
                onChange={handleChange(location.locationId)}
                className={expanded === location.locationId ? 'borderless' : ''}
              >
                <AccordionSummary
                  aria-controls="panel1d-content"
                  id="panel1d-header"
                  className="location-head-title"
                  expandIcon={
                    expanded === location.locationId ? (
                      <MinusIcon />
                    ) : (
                      <PlusIcon />
                    )
                  }
                >
                  <Typography>{location.locationName}</Typography>
                </AccordionSummary>
                <AccordionDetails className="location-details">
                  <Grid
                    size={{
                      xs: 12,
                    }}
                  >
                    <List>
                      {location.places.map((place) => {
                        // START   -------------------------------------------------------------- Opening or pre opening chip
                        const openingOrPreOpeningOffer =
                          place.marketingSupport?.bookingHighlight
                        // END     -------------------------------------------------------------- Opening or pre opening chip

                        return (
                          <React.Fragment key={place.id}>
                            <ListItem className="borderless">
                              <ListItemButton
                                onClick={() => {
                                  selectLocation(place)
                                }}
                              >
                                <ListItemAvatar>
                                  <MapPinIcon />
                                </ListItemAvatar>

                                <ListItemText
                                  className="list-text"
                                  primary={
                                    !isMembership && place.name ? (
                                      <>
                                        {place.name}
                                        {!!openingOrPreOpeningOffer && (
                                          <Chip
                                            label={openingOrPreOpeningOffer}
                                            className="new-location-chip"
                                          />
                                        )}
                                      </>
                                    ) : (
                                      place.name
                                    )
                                  }
                                  secondary={`${
                                    place.address?.streetAddress ?? ''
                                  } ${place.address?.city ?? ''}, ${
                                    place.address?.state ?? ''
                                  } ${place.address?.zipCode ?? ''}`}
                                />

                                {place.id === selectedLocation && (
                                  <ListItemSecondaryAction>
                                    {' '}
                                    <CheckmarkIcon />
                                  </ListItemSecondaryAction>
                                )}
                              </ListItemButton>
                            </ListItem>
                          </React.Fragment>
                        )
                      })}
                    </List>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )
          )
        })}
      </div>
      <div className="footer-continue">
        {!!selectedLocation && (
          <Button
            fullWidth
            variant="contained"
            disableElevation
            className="app-btn"
            onClick={() => {
              isMembership
                ? navigateToEstheticianMembership().catch((e) =>
                    console.error(e)
                  )
                : navigateToNextScreen().catch((e) => console.error(e))
            }}
          >
            Continue
          </Button>
        )}
      </div>
    </>
  )
}
export default React.memo(Locations)
